import type { RouteLocationNormalized, Router } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { loadAccess, isMaintainer } from '@/composables/useAccess'
import { isPreLaunch } from '@/utils/prelaunch'
import { resolveDraftPack } from '@/composables/usePackSelection'
import { packIdFromSlug } from '@/content-packs/registry'
import { preloadPack } from '@/content-packs/resolved'
import { getCampaign } from '@/lib/campaigns'

// INC-105 / ADR-057: routes hidden from public visitors while in pre-launch
// mode. The routes/components stay registered (dev access when the flag is off);
// the guard only redirects navigation to them. A signed-in account passes; creating a
// campaign stays server-checked (ADR-099 D2 as amended).
// `auth` stays reachable.
const PRELAUNCH_BLOCKED = new Set(['builder', 'campaigns', 'campaign'])

export const routes = [
  {
    // INC-007: '/' is now the landing page. The builder moved to '/builder'.
    path: '/',
    name: 'home',
    component: () => import('@/views/LandingView.vue'),
  },
  {
    // INC-D / ADR-066: the pack rides `/builder?pack=<manifest-id>`. `BuilderEntry` gates the wizard
    // behind the shared pack picker when no `?pack` is present; when it IS present (deep-link,
    // reload, or an edit/resume navigation) this guard preloads the pack so the wizard's synchronous
    // store creation resolves it. A missing/unknown `?pack` is left for `BuilderEntry` to resolve.
    path: '/builder',
    name: 'builder',
    component: () => import('@/views/BuilderEntry.vue'),
    beforeEnter: async (to: RouteLocationNormalized) => {
      const pack = resolveDraftPack(packIdFromSlug(to.query.pack))
      if (pack) await preloadPack(pack)
      return true
    },
  },
  {
    path: '/auth',
    name: 'auth',
    component: () => import('@/views/AuthView.vue'),
  },
  {
    path: '/campaigns',
    name: 'campaigns',
    component: () => import('@/views/CampaignsDashboard.vue'),
    meta: { requiresAuth: true },
  },
  {
    // INC-D / ADR-066: route-level pack resolution. Fetch the campaign's `pack_id`, resolve it to a
    // shippable pack, and PRELOAD its chunk before the view mounts, stashing the manifest id on
    // `meta.packId` so `CampaignView` binds the sheet SFCs, engine, and combat helpers synchronously.
    path: '/campaigns/:id',
    name: 'campaign',
    component: () => import('@/views/CampaignView.vue'),
    meta: { requiresAuth: true },
    beforeEnter: async (to: RouteLocationNormalized) => {
      const { data, error } = await getCampaign(to.params.id as string)
      // F1: no row for this account (not a member, RLS hides it) or a deleted campaign → say so on
      // My Campaigns, carrying the campaign id (P4) so a removal notice naming it wins over the
      // generic message. A read error keeps the fallback below.
      if (!error && (!data || data.archived_at)) {
        return { name: 'campaigns', query: { unavailable: to.params.id as string } }
      }
      // ADR-099 F1: a hidden (restricted, no grant) pack never falls back to the default.
      const pack = resolveDraftPack(data?.pack_id ?? 'dnd-4.5-dab')
      if (!pack) return { name: 'campaigns' }
      await preloadPack(pack)
      to.meta.packId = pack
      return true
    },
  },
  {
    // ADR-099: the maintainer's Game codes page (issue, list, revoke pack codes). Not in
    // PRELAUNCH_BLOCKED: the maintainer check is stricter.
    path: '/pack-codes',
    name: 'pack-codes',
    component: () => import('@/views/PackCodesView.vue'),
    meta: { requiresAuth: true },
    beforeEnter: async () => {
      await loadAccess()
      return isMaintainer.value ? true : { name: 'home' }
    },
  },
  {
    // INC-138 / ADR-073: static, prerendered (vite.config.ts includedRoutes).
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/PrivacyView.vue'),
  },
  {
    // INC-190 / ADR-101: the user guide. Static, public, prerendered (vite.config.ts includedRoutes).
    path: '/help',
    name: 'help',
    component: () => import('@/views/HelpView.vue'),
  },
  {
    // INC-138 / ADR-073: double opt-in confirm/unsubscribe landing pages. Not
    // prerendered (token in query) and not in PRELAUNCH_BLOCKED — reachable
    // from an email even while the rest of the site is pre-launch-gated.
    path: '/waitlist/confirm',
    name: 'waitlist-confirm',
    component: () => import('@/views/WaitlistActionView.vue'),
    props: { action: 'confirm' },
  },
  {
    path: '/waitlist/unsubscribe',
    name: 'waitlist-unsubscribe',
    component: () => import('@/views/WaitlistActionView.vue'),
    props: { action: 'unsubscribe' },
  },
  // INC-147: dev-only primitives gallery for tools/qa/screenshots.mjs; absent from builds.
  ...(import.meta.env.DEV
    ? [{ path: '/__qa', name: 'qa', component: () => import('@/views/QaGalleryView.vue') }]
    : []),
]

export function installGuard(router: Router) {
  router.beforeEach(async (to) => {
    // Pre-launch gate: the unfinished features are unreachable to the public.
    // Runs before the auth check so it also covers the requiresAuth routes.
    if (typeof to.name === 'string' && PRELAUNCH_BLOCKED.has(to.name)) {
      await loadAccess() // role and grants are known before any pack list renders (ADR-099)
      if (isPreLaunch) {
        // A signed-in account passes the pre-launch gate (ADR-099 D2 as amended); only a
        // visitor without a token is sent home.
        const { session, ready, ensureReady } = useAuth()
        ensureReady()
        await ready
        if (!session.value?.access_token) return { name: 'home' }
      }
    }
    if (!to.meta.requiresAuth) return true
    const { session, ready, ensureReady } = useAuth()
    ensureReady() // authed route: trigger the (idle-deferred) session load now
    await ready // wait for the initial getSession() before deciding
    // Require an actual access token, not just a truthy session object — without
    // a JWT, protected pages would let the user in only to fail every DB call
    // against RLS as an anonymous request.
    if (!session.value?.access_token) {
      return { name: 'auth', query: { redirect: to.fullPath } }
    }
    return true
  })
}
